import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useProfileSummary } from '@/features/agent-progress/progressHooks';
import { useUploaderProfileSummary } from '@/features/uploader-progress/progressHooks';
import { uploaderApi } from '@/features/uploader/uploader.api';
import { TIER_META, type TierName } from '@/features/agent-progress/tierConstants';
import { Skeleton } from '@/components/chassis/States';
import { useT, useLang, type StringKey } from '@/i18n';

/**
 * F5-R8b · «Mi rango» — la escalera dibujada como escalera (dibujo del CEO
 * «Rango - Tarjeta de inicio», 2026-08-29).
 *
 * Dos tamaños en el mismo componente:
 *  - COMPACTA: botón de grilla (escudo, nombre, total, 4 peldaños miniatura
 *    14/20/26/32px, línea «Faltan N para X», cápsula «Ver mi escalera» con
 *    nudge + ripple + hover invertido + press .97).
 *  - Al tocarla se EXPANDE EN EL LUGAR (no modal): escalera completa
 *    72/96/120/144px con riseIn 520ms/stagger 90ms, relleno 900ms
 *    cubic-bezier(.34,1.56,.64,1), sweep 3.4s + halo 2.6s SOLO en el escalón
 *    actual, marcador «Acá estás» con bob, detalle aria-live, 3 cifras del
 *    pie, chip «Tope de la escalera» con sparkle, glow radial que se corre
 *    según el escalón. prefers-reduced-motion apaga todo y los peldaños
 *    aparecen llenos.
 *
 * DATOS — nada se finge:
 *  - agent:    GET /agents/me/profile-summary (total del mes del propio
 *    agente; posición, próximo umbral y % del tramo los computa el SERVER
 *    contra system_config.agent_tier_thresholds con el ancla firmada).
 *  - uploader: GET /uploader/me/profile-summary (ídem con
 *    uploader_tier_thresholds) + GET /uploader/me/stats para los umbrales
 *    del escalón actual y el próximo (valores reales del config). Disclaimer
 *    FIJO motivacional (su regla F5-T8 / N-Q5).
 *  - Los umbrales de peldaños que NINGÚN endpoint vivo expone se muestran
 *    como «—» (ausentes, no inventados): el server hoy solo entrega el
 *    próximo umbral (+ el actual en uploader/stats). Ver NECESITA-SERVER en
 *    el reporte R8b — exponer el mapa {base,t1,t2,t3} en profile-summary lo
 *    completa sin tocar este componente más que en su adapter.
 *  - navigator / supervisor: los umbrales del dibujo son DEMO — no existen
 *    claves en system_config ni endpoint que dé el total. El componente
 *    acepta el rol pero devuelve null: solo se monta donde hay data real.
 *
 * La rampa teal #cfe4e5 → #8fc3c4 → #4f9fa1 → #228a8d son los MISMOS
 * literales que la tabla de progreso (precedente F5): un nivel tiene el
 * mismo color en todo el sistema. Cero montos: escalones, conteos y días.
 */

export type RangoRole = 'agent' | 'uploader' | 'navigator' | 'supervisor';

type RungKey = 'base' | 't1' | 't2' | 't3';
const ORDER: readonly RungKey[] = ['base', 't1', 't2', 't3'] as const;

/** Mismos literales que la tabla de progreso (precedente F5). */
const RAMP = ['#cfe4e5', '#8fc3c4', '#4f9fa1', '#228a8d'] as const;
const BIG_HEIGHTS = [72, 96, 120, 144] as const;
const MINI_HEIGHTS = [14, 20, 26, 32] as const;

interface RangoInput {
  unitKey: 'f5r8b.unit.screenings' | 'f5r8b.unit.cargas';
  counterKey: 'f5r8b.counter.agent' | 'f5r8b.counter.uploader';
  total: number;
  periodYear: number;
  periodMonth: number;
  nyDate: string;
  /** Del server: próximo escalón (null = tope). */
  nextTier: TierName | null;
  nextThreshold: number | null;
  toNext: number | null;
  /** Del server: % del tramo actual→próximo (misma fórmula del dibujo). */
  progressPct: number;
  bestMonthTotal: number | null;
  /** Umbrales con valor REAL de config conocido por algún endpoint vivo. */
  knownThresholds: Partial<Record<RungKey, number>>;
  motivational: boolean;
}

export function RangoCard({ role, className }: { role: RangoRole; className?: string }): JSX.Element | null {
  if (role === 'agent') return <AgentRango className={className} />;
  if (role === 'uploader') return <UploaderRango className={className} />;
  // navigator / supervisor — los umbrales del dibujo son DEMO: no hay claves
  // en system_config ni fuente viva del total. NECESITA-SERVER (reporte R8b).
  // Nada se finge: el rol se acepta, la tarjeta no se monta.
  return null;
}

// ---------------------------------------------------------------------------
// Adapters por rol — cada rama llama SOLO sus endpoints.
// ---------------------------------------------------------------------------

function AgentRango({ className }: { className?: string }): JSX.Element {
  const q = useProfileSummary();
  if (!q.data) return <RangoSkeleton className={className} />;
  const m = q.data.monthly;
  return (
    <RangoView
      className={className}
      input={{
        unitKey: 'f5r8b.unit.screenings',
        counterKey: 'f5r8b.counter.agent',
        total: m.screeningsMonth,
        periodYear: m.periodYear,
        periodMonth: m.periodMonth,
        nyDate: q.data.today.nyDate,
        nextTier: m.nextTier,
        nextThreshold: m.nextTierThreshold,
        toNext: m.screeningsToNextTier,
        progressPct: m.progressPctToNextTier,
        bestMonthTotal: q.data.achievements.bestMonth?.totalScreenings ?? null,
        knownThresholds: {},
        motivational: false,
      }}
    />
  );
}

function UploaderRango({ className }: { className?: string }): JSX.Element {
  const q = useUploaderProfileSummary();
  // Misma queryKey que el panel de stats existente — cache compartida, cero
  // fetch duplicado. Solo se usan los VALORES de umbral (config real) para
  // etiquetar peldaños; la posición y el % vienen de profile-summary.
  const statsQ = useQuery({
    queryKey: ['uploader', 'me-stats'],
    queryFn: () => uploaderApi.getMyStats(),
    staleTime: 60_000,
  });
  if (!q.data) return <RangoSkeleton className={className} />;
  const m = q.data.monthly;

  const knownThresholds: Partial<Record<RungKey, number>> = {};
  const tg = statsQ.data?.targets;
  if (tg) {
    if (tg.currentTier.name !== 'below_floor' && tg.currentTier.threshold > 0) {
      knownThresholds[tg.currentTier.name] = tg.currentTier.threshold;
    }
    if (tg.nextTier) knownThresholds[tg.nextTier.name] = tg.nextTier.threshold;
  }

  return (
    <RangoView
      className={className}
      input={{
        unitKey: 'f5r8b.unit.cargas',
        counterKey: 'f5r8b.counter.uploader',
        total: m.uploadsMonth,
        periodYear: m.periodYear,
        periodMonth: m.periodMonth,
        nyDate: q.data.today.nyDate,
        nextTier: m.nextTier,
        nextThreshold: m.nextTierThreshold,
        toNext: m.uploadsToNextTier,
        progressPct: m.progressPctToNextTier,
        bestMonthTotal: q.data.achievements.bestMonth?.totalUploads ?? null,
        knownThresholds,
        motivational: q.data.motivationalOnly === true,
      }}
    />
  );
}

function RangoSkeleton({ className }: { className?: string }): JSX.Element {
  return (
    <div
      className={className}
      style={{ background: '#fff', borderWidth: 1.5, borderStyle: 'solid', borderColor: '#e3e8eb', borderRadius: 12, padding: '18px 20px', boxSizing: 'border-box' }}
      data-r8b="rango-skeleton"
    >
      <Skeleton className="h-3 w-24 mb-4" />
      <Skeleton className="h-10 w-40 mb-4" />
      <Skeleton className="h-8 w-full mb-3" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vista compartida — compacta ⇄ expandida en el lugar.
// ---------------------------------------------------------------------------

function RangoView({ input, className }: { input: RangoInput; className?: string }): JSX.Element {
  const t = useT();
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const miniRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Al abrir, el foco va al «Cerrar»; al cerrar, vuelve a la tarjeta.
  // wasOpen evita robar el foco en el primer render de la página.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) {
      closeRef.current?.focus();
      wasOpen.current = true;
    } else if (wasOpen.current) {
      miniRef.current?.focus({ preventScroll: true });
      wasOpen.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const nf = new Intl.NumberFormat(lang === 'es' ? 'es-AR' : 'en-US');
  const unit = t(input.unitKey);
  const monthName = new Date(input.periodYear, input.periodMonth - 1, 1)
    .toLocaleDateString(lang === 'es' ? 'es' : 'en', { month: 'long' });

  // Posición derivada SOLO de campos del server (consistencia garantizada
  // con % y «faltan»): el escalón actual es el anterior al próximo.
  const nextIdx = input.nextTier == null ? -1 : ORDER.indexOf(input.nextTier as RungKey);
  const tierIdx = input.nextTier == null ? 3 : Math.max(-1, nextIdx - 1);
  const isTop = input.nextTier == null;
  const targetKey: RungKey | null = isTop ? null : (ORDER[tierIdx + 1] ?? null);

  // Etiquetas de umbral con valor REAL conocido; el resto queda ausente («—»).
  const labels: Partial<Record<RungKey, number>> = { ...input.knownThresholds };
  if (targetKey && input.nextThreshold != null) labels[targetKey] = input.nextThreshold;

  const curKey: RungKey | null = tierIdx >= 0 ? ORDER[tierIdx]! : null;
  const tierName = curKey
    ? t(TIER_META[curKey].i18nLabelKey as StringKey)
    : t('f5r8b.caminoA', { tier: t('tier.bronce') });
  const crestLetter = curKey ? TIER_META[curKey].emoji : '·';
  const crestBg = curKey ? RAMP[tierIdx]! : '#eef1f3';
  const crestLetterColor = tierIdx >= 2 ? '#fff' : (curKey ? '#1a6a6d' : '#9aa7b2');

  // Días del mes — aritmética real sobre la fecha NY del server.
  const dayOfMonth = Math.max(1, Number(input.nyDate.slice(8, 10)) || 1);
  const daysInMonth = new Date(input.periodYear, input.periodMonth, 0).getDate();
  const daysLeft = Math.max(0, daysInMonth - dayOfMonth);
  const perDay = input.toNext != null ? Math.ceil(Math.max(0, input.toNext) / Math.max(daysLeft, 1)) : 0;
  // Misma fórmula del dibujo (y del panel «Mi progreso» F5-T8): total / día.
  const avg = Math.round((input.total / dayOfMonth) * 10) / 10;
  const avgLabel = avg.toLocaleString(lang === 'es' ? 'es-AR' : 'en-US', { maximumFractionDigits: 1 });

  const clampedPct = Math.max(0, Math.min(100, Math.round(input.progressPct)));

  const rungs = ORDER.map((key, i) => {
    const reached = i <= tierIdx;
    const isTarget = i === tierIdx + 1;
    const isHere = tierIdx === -1 ? i === 0 : i === tierIdx;
    const th = labels[key] ?? null;
    const name = t(TIER_META[key].i18nLabelKey as StringKey);
    let aria: string;
    if (reached) {
      aria = th != null
        ? t('f5r8b.aria.reached', { tier: name, th: nf.format(th), unit })
        : t('f5r8b.aria.reachedNoTh', { tier: name });
    } else if (isTarget && th != null) {
      aria = t('f5r8b.aria.faltan', { tier: name, th: nf.format(th), unit, n: nf.format(Math.max(0, th - input.total)) });
    } else {
      aria = t('f5r8b.aria.pending', { tier: name });
    }
    return { key, i, name, reached, isTarget, isHere, th, aria, color: RAMP[i]! };
  });

  const nextName = targetKey ? t(TIER_META[targetKey].i18nLabelKey as StringKey) : null;
  const miniNext = nextName && input.toNext != null
    ? t('f5r8b.faltanPara', { n: nf.format(input.toNext), tier: nextName })
    : t('f5r8b.tope');
  const miniAria = t('f5r8b.aria.mini', { tier: tierName, total: nf.format(input.total), unit, m: monthName });

  // «El resumen se anuncia una vez al cargar» — texto congelado en la primera
  // pintura para que los refetch no lo re-anuncien.
  const firstAria = useRef<string | null>(null);
  if (firstAria.current === null) firstAria.current = miniAria;

  const detailText = nextName && input.nextThreshold != null
    ? t('f5r8b.detail.next', {
        need: nf.format(Math.max(0, input.toNext ?? 0)),
        tier: nextName,
        th: nf.format(input.nextThreshold),
        unit,
        d: daysLeft,
        perDay: nf.format(perDay),
      })
    : t('f5r8b.detail.top', { tier: tierName, d: daysLeft, m: monthName });

  const footStats: Array<{ value: string; label: string }> = [
    { value: avgLabel, label: t('f5r8b.foot.avg') },
    isTop
      ? { value: String(daysLeft), label: t('f5r8b.foot.daysLeft') }
      : { value: nf.format(perDay), label: t('f5r8b.foot.perDay', { tier: nextName ?? '' }) },
    { value: input.bestMonthTotal != null ? nf.format(input.bestMonthTotal) : '—', label: t('f5r8b.foot.best') },
  ];

  // F5-R9u (caminata CEO): la compacta va SIN height:100% inline — un height
  // explícito apaga el align-items:stretch del padre flex y el % no resuelve
  // contra altura auto, así la tarjeta quedaba más baja que «Mi progreso».
  // Con cross-size auto el stretch del padre la iguala y .r8b-mini la llena.
  return (
    <div
      className={`r8b-root ${className ?? ''}`}
      style={open ? { gridColumn: '1 / -1', flexBasis: '100%', maxWidth: '100%' } : undefined}
      data-r8b={open ? 'rango-full' : 'rango-mini'}
    >
      <style>{STYLE_CSS}</style>
      <span className="sr-only" aria-live="polite">{firstAria.current}</span>
      {open ? (
        <RangoFull
          t={t}
          nf={nf}
          input={input}
          rungs={rungs}
          tierIdx={tierIdx}
          tierName={tierName}
          crestLetter={crestLetter}
          crestBg={crestBg}
          crestLetterColor={crestLetterColor}
          isTop={isTop}
          monthName={monthName}
          detailText={detailText}
          footStats={footStats}
          clampedPct={clampedPct}
          onClose={() => setOpen(false)}
          closeRef={closeRef}
        />
      ) : (
        <RangoMini
          t={t}
          nf={nf}
          input={input}
          rungs={rungs}
          tierName={tierName}
          crestLetter={crestLetter}
          crestBg={crestBg}
          crestLetterColor={crestLetterColor}
          monthName={monthName}
          miniNext={miniNext}
          miniAria={miniAria}
          clampedPct={clampedPct}
          onOpen={() => setOpen(true)}
          miniRef={miniRef}
        />
      )}
    </div>
  );
}

type BuiltRung = {
  key: RungKey; i: number; name: string; reached: boolean; isTarget: boolean;
  isHere: boolean; th: number | null; aria: string; color: string;
};
type TFn = ReturnType<typeof useT>;

/** El relleno interpola 0 → % real tras el primer frame; con
 *  prefers-reduced-motion arranca lleno (los peldaños aparecen ya llenos). */
function useArmedFill(): boolean {
  const reduce = typeof window !== 'undefined'
    && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [armed, setArmed] = useState(reduce);
  useEffect(() => {
    if (armed) return;
    const id = requestAnimationFrame(() => setArmed(true));
    return () => cancelAnimationFrame(id);
  }, [armed]);
  return armed;
}

function fillPctFor(r: BuiltRung, clampedPct: number, minPct: number): number {
  if (r.reached) return 100;
  if (r.isTarget) return Math.max(minPct, clampedPct);
  return 0;
}

// ---------------------------------------------------------------------------
// Compacta — botón de grilla.
// ---------------------------------------------------------------------------

function RangoMini(props: {
  t: TFn; nf: Intl.NumberFormat; input: RangoInput; rungs: BuiltRung[];
  tierName: string; crestLetter: string; crestBg: string; crestLetterColor: string;
  monthName: string; miniNext: string; miniAria: string; clampedPct: number;
  onOpen: () => void; miniRef: React.RefObject<HTMLButtonElement>;
}): JSX.Element {
  const { t, nf, input, rungs, tierName, crestLetter, crestBg, crestLetterColor,
    monthName, miniNext, miniAria, clampedPct, onOpen, miniRef } = props;
  const armed = useArmedFill();

  return (
    <button
      ref={miniRef}
      type="button"
      className="r8b-mini font-body"
      onClick={onOpen}
      aria-label={miniAria}
      aria-expanded={false}
      data-r8b="mini-btn"
    >
      {/* glow estático arriba a la derecha */}
      <span aria-hidden style={{ position: 'absolute', top: '-50%', right: '-10%', width: '55%', height: '200%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(34,138,141,.09), rgba(34,138,141,0) 70%)', pointerEvents: 'none' }} />
      <span style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
          <span className="font-head" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: '#228a8d' }}>{t('f5r8b.title')}</span>
          <span className="font-head" style={{ flex: 'none', background: '#eef1f3', color: '#6b7c8c', borderRadius: 999, padding: '3px 9px', fontSize: 10, fontWeight: 700 }}>{monthName}</span>
        </span>

        <span style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <span style={{ position: 'relative', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 10, background: crestBg, borderWidth: 1.5, borderStyle: 'solid', borderColor: crestBg === '#eef1f3' ? '#e3e8eb' : crestBg, overflow: 'hidden' }}>
            <span aria-hidden className="r8b-crestsweep" />
            <span className="font-head" style={{ position: 'relative', fontSize: 15, fontWeight: 800, color: crestLetterColor }}>{crestLetter}</span>
          </span>
          <span style={{ minWidth: 0, textAlign: 'left' }}>
            <span className="font-head" style={{ display: 'block', fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em', color: '#122b44' }}>{tierName}</span>
            <span style={{ display: 'block', marginTop: 2, fontSize: 12, color: '#6b7c8c', fontVariantNumeric: 'tabular-nums' }}>{nf.format(input.total)} {t(input.unitKey)}</span>
          </span>
        </span>

        {/* 4 peldaños miniatura — la escalera vista de lejos, mismo cálculo. */}
        <span style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 32, marginBottom: 12 }} aria-hidden>
          {rungs.map((r) => (
            <span
              key={r.key}
              className="r8b-riseMini"
              style={{ position: 'relative', flex: 1, minWidth: 0, height: MINI_HEIGHTS[r.i], borderRadius: 5, background: '#eef1f3', borderWidth: 1, borderStyle: 'solid', borderColor: r.isHere ? r.color : '#e3e8eb', overflow: 'hidden', animationDelay: `${r.i * 0.07}s`, display: 'block' }}
            >
              <span className="r8b-fill" style={{ height: `${armed ? fillPctFor(r, clampedPct, 6) : 0}%`, background: r.color, display: 'block' }}>
                {r.isHere && <span aria-hidden className="r8b-sweep" style={{ width: '40%', background: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.6), rgba(255,255,255,0))' }} />}
              </span>
            </span>
          ))}
        </span>

        <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginTop: 'auto' }}>
          <span style={{ fontSize: 12, color: '#6b7c8c', fontVariantNumeric: 'tabular-nums' }}>{miniNext}</span>
          {/* Cápsula «Ver mi escalera» — reposo: nudge de flecha + ripple;
              hover: se invierte a teal sólido, sube 1px, se abre el gap;
              press: .97 y teal oscuro. Los tres estados viven en la cápsula. */}
          <span className="r8b-cap font-head">
            <span aria-hidden className="r8b-ripple" />
            <span style={{ position: 'relative', whiteSpace: 'nowrap' }}>{t('f5r8b.verEscalera')}</span>
            <span aria-hidden className="r8b-arrow">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
            </span>
          </span>
        </span>
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Expandida — la escalera completa, en el lugar de la grilla.
// ---------------------------------------------------------------------------

function RangoFull(props: {
  t: TFn; nf: Intl.NumberFormat; input: RangoInput; rungs: BuiltRung[];
  tierIdx: number; tierName: string; crestLetter: string; crestBg: string;
  crestLetterColor: string; isTop: boolean; monthName: string; detailText: string;
  footStats: Array<{ value: string; label: string }>; clampedPct: number;
  onClose: () => void; closeRef: React.RefObject<HTMLButtonElement>;
}): JSX.Element {
  const { t, nf, input, rungs, tierIdx, tierName, crestLetter, crestBg,
    crestLetterColor, isTop, monthName, detailText, footStats, clampedPct,
    onClose, closeRef } = props;
  const armed = useArmedFill();

  return (
    <div className="r8b-fadeup font-body" style={{ position: 'relative', background: '#fff', borderWidth: 1.5, borderStyle: 'solid', borderColor: '#e3e8eb', borderRadius: 14, padding: '26px 28px 24px', overflow: 'hidden', color: '#122b44' }} data-r8b="full-card">
      {/* glow radial que se corre según el escalón */}
      <span aria-hidden className="r8b-glowmove" style={{ position: 'absolute', top: '-40%', left: `${12 + (tierIdx + 1) * 20}%`, width: '46%', height: '180%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(34,138,141,.10), rgba(34,138,141,0) 70%)', pointerEvents: 'none' }} />

      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button
            ref={closeRef}
            type="button"
            className="r8b-collapse font-head"
            onClick={onClose}
            aria-label={t('f5r8b.cerrarAria')}
            data-r8b="close-btn"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flex: 'none' }}><path d="m18 15-6-6-6 6" /></svg>
            {t('f5r8b.cerrar')}
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', marginBottom: 24 }}>
          <div style={{ minWidth: 0 }}>
            <p className="font-head" style={{ margin: '0 0 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: '#228a8d' }}>{t('f5r8b.kicker', { m: monthName })}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 13, flexWrap: 'wrap' }}>
              <span style={{ position: 'relative', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, borderRadius: 14, background: crestBg, borderWidth: 1.5, borderStyle: 'solid', borderColor: crestBg === '#eef1f3' ? '#e3e8eb' : crestBg, overflow: 'hidden' }}>
                <span aria-hidden className="r8b-crestsweep" />
                <span className="font-head" style={{ position: 'relative', fontSize: 20, fontWeight: 800, color: crestLetterColor }}>{crestLetter}</span>
              </span>
              <div style={{ minWidth: 0 }}>
                <p className="font-head" style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em' }}>{tierName}</p>
                <p style={{ margin: '3px 0 0', fontSize: 13, color: '#6b7c8c' }}>
                  {tierIdx >= 0 ? t('f5r8b.escalonDe', { m: monthName }) : t('f5r8b.sinEscalon')}
                </p>
              </div>
              {isTop && (
                <span className="font-head" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#e2f0f0', color: '#1a6a6d', border: '1px solid #c9e2e2', borderRadius: 999, padding: '5px 12px', fontSize: 11, fontWeight: 700 }}>
                  <span aria-hidden className="r8b-sparkle">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}><path d="M12 3v4" /><path d="M12 17v4" /><path d="M3 12h4" /><path d="M17 12h4" /><path d="m6.3 6.3 2.8 2.8" /><path d="m14.9 14.9 2.8 2.8" /></svg>
                  </span>
                  {t('f5r8b.tope')}
                </span>
              )}
            </div>
          </div>

          <div style={{ flex: 'none', textAlign: 'right', minWidth: 150 }}>
            <span className="font-head" style={{ display: 'block', fontSize: 34, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>{nf.format(input.total)}</span>
            <span style={{ display: 'block', marginTop: 6, fontSize: 12.5, lineHeight: 1.4, color: '#6b7c8c' }}>{t(input.counterKey, { m: monthName })}</span>
          </div>
        </div>

        {/* La escalera: cada escalón es una COLUMNA (bloque + nombre + número
            hijos del mismo contenedor — la etiqueta no puede desalinearse). */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 22 }}>
          {rungs.map((r) => {
            const fillPct = fillPctFor(r, clampedPct, 4);
            const pctText = r.reached ? '100%' : (r.isTarget ? `${fillPct}%` : '');
            const pctColor = fillPct > 22 ? (r.i >= 2 ? '#fff' : '#1a6a6d') : '#6b7c8c';
            const highlight = r.isHere;
            return (
              <button
                key={r.key}
                type="button"
                className="r8b-rise"
                aria-label={r.aria}
                aria-pressed={r.isHere}
                style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch', animationDelay: `${r.i * 0.09}s`, background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'default' }}
              >
                <span style={{ display: 'block', height: 26, textAlign: 'center' }}>
                  {r.isHere && (
                    <span className="r8b-marker font-head" style={{ display: 'inline-block', background: '#122b44', color: '#fff', borderRadius: 999, padding: '4px 11px', fontSize: 10, fontWeight: 800, letterSpacing: '.04em', whiteSpace: 'nowrap' }}>{t('f5r8b.acaEstas')}</span>
                  )}
                </span>

                <span style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: BIG_HEIGHTS[r.i], borderRadius: 10, background: '#eef1f3', borderWidth: 1.5, borderStyle: 'solid', borderColor: highlight ? r.color : '#e3e8eb', overflow: 'hidden' }}>
                  {r.isHere && <span aria-hidden className="r8b-halo" style={{ background: r.color }} />}
                  <span className="r8b-fill" style={{ height: `${armed ? fillPct : 0}%`, background: r.color, display: 'block' }}>
                    {r.isHere && <span aria-hidden className="r8b-sweep" style={{ width: '38%', background: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.55), rgba(255,255,255,0))' }} />}
                  </span>
                  <span className="font-head" style={{ position: 'relative', paddingBottom: 8, fontSize: 11.5, fontWeight: 800, color: pctColor, fontVariantNumeric: 'tabular-nums' }}>{pctText}</span>
                </span>

                <span className="font-head" style={{ display: 'block', marginTop: 10, textAlign: 'center', fontSize: 12, fontWeight: r.isHere ? 800 : 700, color: r.reached || r.isTarget ? '#122b44' : '#6b7c8c' }}>{r.name}</span>
                <span style={{ display: 'block', marginTop: 3, textAlign: 'center', fontSize: 11, fontWeight: 500, color: '#6b7c8c', fontVariantNumeric: 'tabular-nums' }}>{r.th != null ? nf.format(r.th) : '—'}</span>
              </button>
            );
          })}
        </div>

        {/* Lo que dice en palabras — aria-live; número y texto QUIETOS. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#f4fafa', borderWidth: 1.5, borderStyle: 'solid', borderColor: '#c9e2e2', borderRadius: 12, padding: '15px 17px', fontSize: 13.5, lineHeight: 1.55, color: '#1a6a6d', fontVariantNumeric: 'tabular-nums' }}>
          <span aria-hidden style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', background: '#fff', color: '#1a6a6d' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}><path d="M12 20V10" /><path d="M18 20V4" /><path d="M6 20v-4" /></svg>
          </span>
          <span aria-live="polite" style={{ flex: 1, minWidth: 220 }}>{detailText}</span>
        </div>

        {/* Tres cifras del pie: la escalera motiva; estas dicen si es alcanzable. */}
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 20, paddingTop: 18, borderTop: '1px solid #eef1f3' }}>
          {footStats.map((f) => (
            <span key={f.label} style={{ flex: '1 1 150px', minWidth: 140 }}>
              <span className="font-head" style={{ display: 'block', fontSize: 19, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{f.value}</span>
              <span style={{ display: 'block', marginTop: 5, fontSize: 12, lineHeight: 1.4, color: '#6b7c8c' }}>{f.label}</span>
            </span>
          ))}
        </div>

        {input.motivational && (
          <p style={{ margin: '16px 0 0', padding: '13px 15px', background: '#fafbfc', borderWidth: 1.5, borderStyle: 'solid', borderColor: '#e3e8eb', borderRadius: 10, fontSize: 12.5, lineHeight: 1.55, color: '#6b7c8c' }}>
            {t('f5r8b.disc1')}<b>{t('f5r8b.discBold')}</b>{t('f5r8b.disc2')}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Motion del dibujo — cada movimiento con su razón; prefers-reduced-motion
// apaga TODO dentro de .r8b-root y deja solo el cambio de color.
// ---------------------------------------------------------------------------

const STYLE_CSS = `
@keyframes r8b-fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
@keyframes r8b-riseIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
@keyframes r8b-sweep { 0% { transform:translateX(-170%) skewX(-16deg); } 60%,100% { transform:translateX(260%) skewX(-16deg); } }
@keyframes r8b-bob { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-4px); } }
@keyframes r8b-halo { 0%,100% { opacity:.30; transform:scale(1); } 50% { opacity:.08; transform:scale(1.10); } }
@keyframes r8b-sparkle { 0%,100% { opacity:.3; transform:scale(.85) rotate(0deg); } 50% { opacity:1; transform:scale(1) rotate(16deg); } }
@keyframes r8b-nudge { 0%,72%,100% { transform:translateX(0); } 84% { transform:translateX(4px); } }
@keyframes r8b-ripple { 0% { opacity:.42; transform:scale(.35); } 100% { opacity:0; transform:scale(2.6); } }

.r8b-root *:focus-visible { outline:2px solid #228a8d; outline-offset:2px; }

.r8b-mini {
  position:relative; display:block; width:100%; height:100%; box-sizing:border-box;
  text-align:left; background:#fff; border:1.5px solid #e3e8eb; border-radius:12px;
  padding:18px 20px; cursor:pointer; overflow:hidden; color:#122b44;
  transition:border-color .16s, box-shadow .18s, transform .18s;
}
.r8b-mini:hover {
  border-color:#228a8d;
  box-shadow:0 10px 26px rgba(18,43,68,.11);
  transform:translateY(-3px);
}

.r8b-cap {
  position:relative; display:inline-flex; align-items:center; gap:7px; flex:none;
  min-height:32px; padding:0 12px; border-radius:999px; background-color:#e2f0f0;
  border:1.5px solid #c9e2e2; overflow:hidden; font-size:11.5px; font-weight:700;
  color:#1a6a6d;
  transition:background-color .16s, border-color .16s, color .16s, transform .16s, box-shadow .18s, gap .16s, padding .16s;
}
.r8b-cap:hover {
  gap:9px; padding:0 14px; background-color:#228a8d; border-color:#228a8d;
  color:#fff; transform:translateY(-1px); box-shadow:0 6px 16px rgba(34,138,141,.28);
}
.r8b-cap:active {
  background-color:#1a6a6d; border-color:#1a6a6d; color:#fff;
  transform:scale(.97); box-shadow:none;
  transition:transform .1s, background-color .1s;
}
.r8b-ripple {
  position:absolute; top:50%; left:50%; width:120%; aspect-ratio:1 / 1;
  margin-top:-60%; margin-left:-60%; border-radius:50%; background:#228a8d;
  opacity:0; animation:r8b-ripple 2.8s ease-out infinite; pointer-events:none;
}
.r8b-arrow { position:relative; display:inline-flex; animation:r8b-nudge 3.2s ease-in-out infinite; }

.r8b-fill { position:absolute; left:0; right:0; bottom:0; overflow:hidden; transition:height .9s cubic-bezier(.34,1.56,.64,1); }
.r8b-sweep { position:absolute; top:0; bottom:0; animation:r8b-sweep 3.4s ease-in-out infinite; }
.r8b-crestsweep { position:absolute; top:0; bottom:0; width:40%; background:linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.5), rgba(255,255,255,0)); animation:r8b-sweep 4.2s ease-in-out infinite; }
.r8b-halo { position:absolute; inset:-8px; border-radius:14px; opacity:.2; animation:r8b-halo 2.6s ease-in-out infinite; pointer-events:none; }
.r8b-marker { animation:r8b-bob 2.8s ease-in-out infinite; }
.r8b-sparkle { display:inline-flex; animation:r8b-sparkle 2.2s ease-in-out infinite; }
.r8b-rise { animation:r8b-riseIn .52s cubic-bezier(.34,1.2,.64,1) both; }
.r8b-riseMini { animation:r8b-riseIn .45s cubic-bezier(.34,1.2,.64,1) both; }
.r8b-fadeup { animation:r8b-fadeUp .45s ease both; }
.r8b-glowmove { transition:left .9s cubic-bezier(.4,0,.2,1); }

.r8b-collapse {
  display:inline-flex; align-items:center; gap:7px; min-height:36px; padding:0 11px;
  border:0; background:none; border-radius:8px; font-size:12px; font-weight:700;
  color:#6b7c8c; cursor:pointer; transition:background-color .14s, color .14s;
}
.r8b-collapse:hover { background-color:#f2f5f7; color:#122b44; }

@media (prefers-reduced-motion: reduce) {
  .r8b-root, .r8b-root * { animation:none !important; transition:none !important; }
}
`;
